<template>
  <!-- Мобильная вкладка «Маршруты»: в покое - нижняя панель с полем поиска,
       прижатая к низу карты прямо над таб-баром App.vue (одна поверхность: тот
       же фон, без зазора и без ручки; верхняя граница и скругление - у этой
       панели, у таб-бара под ней своей границы нет, см. AppTabBar `joined`).
       Тап по полю - поиск на весь экран (SearchPanel), таб-бар под ним скрыт. -->
  <div class="md:hidden contents">
    <div
      v-if="!session.open"
      ref="dockEl"
      data-mobile-dock
      class="absolute inset-x-0 bottom-0 z-40 bg-card border-t border-x border-border rounded-t-xl px-3 pt-3 pb-2"
    >
      <!-- Настоящий input: фокус открывает клавиатуру, а перевод фокуса в поле
           развёрнутого поиска (тот же жест) её не закрывает - так iOS не теряет
           клавиатуру, как было бы при focus() вне жеста. -->
      <div class="relative rounded-xl bg-muted/70">
        <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-muted-foreground pointer-events-none" aria-hidden="true" />
        <input
          data-search-trigger
          type="search"
          :value="session.query"
          :placeholder="$t('leftPanel.searchPlaceholder')"
          :aria-label="$t('leftPanel.searchPlaceholder')"
          class="search-own-clear w-full h-11 pl-10 pr-3 bg-transparent rounded-xl text-[15px] outline-none"
          @focus="openSearch"
        />
      </div>
    </div>

    <!-- Развёрнутый поиск: на весь экран поверх карты и таб-бара (z выше
         nav z-50 в App.vue). Только на ширине телефона - на десктопе тот же
         session.open обслуживает левая панель, второй SearchPanel слал бы
         дублирующие запросы. -->
    <div
      v-if="session.open && isPhone"
      data-search-layer
      class="fixed inset-0 z-[70] bg-background pt-safe flex flex-col"
    >
      <SearchPanel
        ref="panelRef"
        variant="mobile"
        class="flex-1 min-h-0"
        @cancel="onCancel"
        @open-route="(id) => emit('open-route', id)"
        @open-point="(id) => emit('open-point', id)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { Search } from "lucide-vue-next";
import SearchPanel from "./search/SearchPanel.vue";
import { mapSheetCover, publishMapSheetInset, releaseMapSheetInset } from "../composables/useMapSheetInset";
import { useSearchSessionStore } from "../store/searchSession";
import { trackEvent } from "../analytics";
import { useIsPhone } from "../composables/useIsPhone";
import { leaveSearchEntry, pushSearchEntry } from "../composables/searchHistoryEntry";
import { useRoute, useRouter } from "vue-router";

const emit = defineEmits<{
  (e: "open-route", routeId: number): void;
  (e: "open-point", id: string): void;
}>();

const session = useSearchSessionStore();
const panelRef = ref<InstanceType<typeof SearchPanel> | null>(null);

const isPhone = useIsPhone();
const route = useRoute();
const router = useRouter();

// Тап по полю в покое: поиск разворачивается, фокус переходит в его поле.
// Развёрнутый поиск - своя запись истории (searchHistoryEntry.ts): «назад»
// браузера закрывает его. push не ждём - фокус должен остаться в жесте (iOS).
// Возврат из карточки (consumeReturn) открывает поиск без фокуса - клавиатура
// не всплывает сама собой - и без новой записи (он возвращается на прежнюю).
const openSearch = async () => {
  session.openSearch();
  trackEvent("Route: Search");
  if (isPhone.value) void pushSearchEntry(router, route);
  await nextTick();
  panelRef.value?.focus();
};
// «Отмена» (сессию уже сбросила SearchPanel): снять запись поиска, если она наша.
const onCancel = () => {
  session.cancel();
  leaveSearchEntry(router);
};

// Высота панели = нижний отступ видимой карты (--map-sheet-inset): тулбар карты
// центруется по высоте над полем. Таб-бар под панелью вне карты - его высоту не
// считаем. ResizeObserver, а не одиночный замер: на первом рендере высота
// ещё 0 (раскладка не готова).
const dockEl = ref<HTMLElement | null>(null);
let observer: ResizeObserver | null = null;
let published = 0;
const insetOwner = Symbol("search-dock");
const publish = (h: number) => {
  published = h;
  publishMapSheetInset(insetOwner, h);
  mapSheetCover.value = h;
};
const release = () => {
  releaseMapSheetInset(insetOwner);
  if (mapSheetCover.value === published) mapSheetCover.value = 0;
  published = 0;
};
watch(
  dockEl,
  (el) => {
    observer?.disconnect();
    observer = null;
    // Поиск открыт - карты не видно: устаревший отступ панели снимается.
    if (!el) {
      release();
      return;
    }
    publish(el.offsetHeight);
    if (typeof ResizeObserver === "undefined") return;
    observer = new ResizeObserver(() => publish(el.offsetHeight));
    observer.observe(el);
  },
  { flush: "post" }
);
onMounted(() => {
  if (dockEl.value) publish(dockEl.value.offsetHeight);
});
onBeforeUnmount(() => {
  observer?.disconnect();
  release();
});
</script>
